import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from "@angular/core";
import { FormControl, FormGroup, Validators } from "@angular/forms";
import { Store } from "@ngrx/store";
import { Observable, Subject, Subscription, takeUntil } from "rxjs";
import { AdobeConstants } from "../../../shared/adobe-tagging/AdobeConstants";
import { AdobeHelpers } from "../../../shared/adobe-tagging/adobeHelper";
import { TextInputModel } from "../../../shared/components/controls/text-input-control/TextInputModel";
import { selectViewInContext } from "../../../shared/reducers";
import { Constants, PageNameTypes, templateNicknameErrorMap, pageInContext } from "../../../shared/utilities/Constants";
import { isEmptyString } from "../../../shared/utilities/FormValidators";
import { nicknameAllowedCharacters } from "../../../shared/utilities/regex-patterns";
import { ConfirmationPageActions, WireActions } from "../../actions";
import { createControl } from "../../utilities/formUtilities";
import { selectIsEmployee, selectIsDemoUser } from '../../../account-info-header/reducers';
import { ToastrService } from 'ngx-toastr';

interface SaveAsTemplateModalForm {
  templateNickname?: FormControl<string | null>;
}

const templateNicknameControl = 'templateNickname';

@Component({
  standalone: false,
  selector: 'mm-save-as-template-modal',
  templateUrl: './save-as-template-modal.component.html',
  styleUrls: ['./save-as-template-modal.component.scss']
})
export class SaveAsTemplateModalComponent implements OnInit, OnDestroy {
  @Input() showModal: Observable<boolean>;
  @Input() title = 'Save this as a template';
  @Output() saveTemplate = new EventEmitter<boolean>();
  @Output() saveTemplateModalClosed = new EventEmitter<{
    cancel: boolean;
    saveClicked: boolean;
  }>();
  saveAsTemplateModalForm = new FormGroup<SaveAsTemplateModalForm>({});
  textBody: string = 'Do you want to name this template so that it can be easily recognized for future use?';
  destroy$: Subject<boolean> = new Subject<boolean>();
  modal: EverestModal;
  showModalSub: Subscription;
  saveClicked: boolean = false;
  viewState: PageNameTypes;
  nicknameModel: TextInputModel = {
    controlName: templateNicknameControl,
    label: 'Nickname',
    hasGreyText: true,
    greyText: '(optional)',
    htmlMaxLength: 40,
    allowedChars: nicknameAllowedCharacters,
  };
  isEmployee: boolean;
  isDemoUser: boolean;

  constructor(
    private store: Store,
    private toastr: ToastrService,
  ) { }

  ngOnInit(): void {
    createControl(this.saveAsTemplateModalForm, templateNicknameControl, '', [Validators.pattern(nicknameAllowedCharacters), isEmptyString]);

    this.store.select(selectViewInContext).pipe(takeUntil(this.destroy$)).subscribe((view) => {
      this.viewState = view;
    });

    this.store.select(selectIsEmployee).subscribe(value => {
      this.isEmployee = value;
    });

    this.store.select(selectIsDemoUser).subscribe(value => {
      this.isDemoUser = value;
    });

    this.showModalSub = this.showModal.subscribe((show) => {
      this.modal = document.getElementById('save-as-template-modal') as EverestModal;
      this.saveClicked = false;
      if (show) {
        this.modal.showModal()
      };
    });
  }

  ngOnDestroy(): void {
    this.showModalSub?.unsubscribe();
  }

  onCancelClick(): void {
    this.hideModal();
  }

  onSaveTemplateClick(): void {
    if (this.nicknameControl?.valid){
      AdobeHelpers.fireClick(AdobeConstants.ADOBE_ + AdobeConstants._SAVETEMPLATE);
      if (this.isDemoUser) {
        this.toastr.error(Constants.toastrMessageDemoUser);
      }
      else if (this.isEmployee) {
        this.toastr.error(Constants.toastrMessageEmployee);
      }
      else {
        if (this.viewState === pageInContext.confirmation){
          this.store.dispatch(ConfirmationPageActions.updateTemplateNickname({ nickname: this.nicknameControl?.value?.trim() ?? '' }));
        } else if (this.viewState === pageInContext.input) {
          this.store.dispatch(WireActions.updateWiresTemplateNickname({ nickname: this.nicknameControl?.value?.trim() ?? '' }));
        }
        this.saveTemplate.emit(true);
      }

      this.saveClicked = true;
      this.hideModal();
    }
  }

  onModalClose(event: any) {
    this.saveTemplateModalClosed.emit({
        cancel: true,
        saveClicked: this.saveClicked,
    });
    this.nicknameControl?.reset('');
  }

  get nicknameControl(): FormControl<string | null> | undefined {
    return this.saveAsTemplateModalForm.controls.templateNickname;
  }

  get nicknameError() {
    if (this.nicknameControl && this.nicknameControl.errors) {
      const errorKeys: string[] = Object.keys(this.nicknameControl.errors ?? {});
      if (errorKeys.length > 0) {
        return templateNicknameErrorMap[
          errorKeys[0] as keyof typeof templateNicknameErrorMap
        ];
      }
    }
  }

  hideModal() {
    this.modal.hideModal(); //in seperate function in order to mock it in unit tests
  }
}

type EverestModal = HTMLElement & {
  showModal: () => {},
  hideModal: () => {}
};
